import React, { useState, useEffect, useRef } from "react";
import _ from "lodash";
import {
  _getSelectedOption,
  _getBaseUrl,
  _isFirstRender,
  _getCSRFTokenValue,
} from "../utils/helper";
import api from "../utils/api";
import { Loader, Pagination } from "../atoms";
import { FilterDropDown, KeywordSearch } from "../forms";
import { CardLibrary } from "../cards";
import StateView from "../utils/StateView";

let INITIAL_VALUES = {
  gebieden: [],
  onderwerpen: [],
  skip: 0,
  take: 10,
  nodeType: "",
  group: "",
  keywords: [],
};

const Actueel = (props) => {
  const [filterOptions, setFilterOptions] = useState([]);
  const [values, setValues] = useState(INITIAL_VALUES);
  const [posts, setPosts] = useState([]);
  const [totalPosts, setTotalPosts] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);
  const [isLoadingPosts, setIsLoadingPosts] = useState(false);
  const [isFilter, setIsFilter] = useState(false);
  const [tags, setTags] = useState([]);
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const isFirstLoad = _isFirstRender();
  const nodeType = props?.data["node-type"] || "";
  const group = props?.data["group"] || "";
  const observerRef = useRef(null);

  useEffect(() => {
    fetchFilters();

    const urlParams = new URLSearchParams(window.location.search);
    const keyword = decodeURIComponent(urlParams.get("keyword"));
    const topic = decodeURIComponent(urlParams.get("topic"));

    if (keyword !== null && keyword !== "" && keyword !== "null") {
      setValues({ ...INITIAL_VALUES, ["keywords"]: [keyword], ["skip"]: 0 });
      addRemoveTags("keywords", keyword, true);
      return;
    }

    if (topic !== null && topic !== "" && topic !== "null") {
      setValues({ ...INITIAL_VALUES, ["topics"]: [topic], ["skip"]: 0 });
      return;
    }

    // setValues({ ...INITIAL_VALUES, ["nodeType"]: nodeType, ["group"]: group });

    // if (nodeType === "actueel") {
    setValues({ ...INITIAL_VALUES, ["take"]: 12, ["nodeType"]: nodeType });
    // }

    // fetchPosts();
  }, []);

  useEffect(() => {
    if (!isFirstLoad) {
      fetchPosts();
    }
  }, [values]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && posts.length < totalPosts && !isLoadingPosts) {
          setValues((prevValues) => ({ ...prevValues, skip: prevValues.skip + prevValues.take }));
        }
      },
      { threshold: 1.0 }
    );

    if (observerRef.current) {
      observer.observe(observerRef.current);
    }

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current);
      }
    };
  }, [posts, totalPosts, isLoadingPosts]);

  const fetchFilters = () => {
    setIsLoadingFilters(true);

    api
      .get("/filterlistoptions")
      .then((res) => {
        setIsLoadingFilters(false);
        setFilterOptions(res.data);

        const urlParams = new URLSearchParams(window.location.search);
        const topic = decodeURIComponent(urlParams.get("topic"));

        if (topic !== null && topic !== "" && topic !== "null") {
          addRemoveTags("topics", topic, true);
        }
      })
      .catch(() => {
        setIsLoadingFilters(false);
      });
  };

  const fetchPosts = async () => {
    setIsLoadingPosts(true);

    const csrfToken = await _getCSRFTokenValue();
    const fields = values;
    api
      .post("/searchandfilter", fields, {
        headers: {
          "X-CSRF-Token": csrfToken,
        },
      })
      .then((res) => {
        setIsLoadingPosts(false);
        setPosts((prevPosts) =>
          values.skip === 0 ? res.data.posts : [...prevPosts, ...res.data.posts]
        );
        setTotalPosts(res.data.totalNodes);
      })
      .catch(() => {
        setIsLoadingPosts(false);
      });
  };

  const updateFilters = (key, value) => {
    let filters = { ...values, [key]: value, skip: 0 };

    setCurrentPage(1);
    setPosts([]); // Clear existing posts when filters change
    setValues(filters);
  };

  const addRemoveTags = (filter, value, addRemove) => {
    let temp = [...tags];

    if (addRemove) {
      temp.push({
        filter,
        value,
      });
    } else {
      temp = temp.filter((item) => !(item.filter === filter && item.value === value));
    }

    setTags(temp);
  };

  const onFilterChanged = (filter, value, checked) => {
    let updatedValue = [...values[filter]];

    if (checked) {
      addRemoveTags(filter, value, true);
      updatedValue.push(value);
    } else {
      addRemoveTags(filter, value, false);
      updatedValue = updatedValue.filter((item) => item !== value);
    }

    updateFilters(filter, updatedValue);
    setIsFilter(true);
  };

  const onKeyUpHandler = (e) => {
    if (e.key === "Enter") {
      const keyword = e.target.value;
      setSearchValue(keyword);
      const isWhitespaceString = !keyword.replace(/\s/g, "").length;

      if (isWhitespaceString) {
        return;
      }

      let updatedKeywords = [...values.keywords];

      if (updatedKeywords.indexOf(keyword) >= 0) {
        return;
      }

      updatedKeywords.push(keyword);

      updateFilters("keywords", updatedKeywords);
      addRemoveTags("keywords", keyword, true);
      setIsFilter(true);

      e.target.value = "";
    }
  };

  const removeKeyword = (keyword) => {
    let updatedKeywords = [...values.keywords];

    updatedKeywords = updatedKeywords.filter((item) => item !== keyword);

    updateFilters("keywords", updatedKeywords);
    addRemoveTags("keywords", keyword, false);

    if (updatedKeywords.length === 0) {
      setSearchValue("");
    }
  };

  const clearFilters = () => {
    setSearchValue("");
    setValues({
      ...INITIAL_VALUES,
      nodeType: values.nodeType,
      group: values.group,
    });
    setTags([]);
    setIsFilter(false);
  };

  const navigatePage = (goNext) => {
    let page;
    let skip;

    if (goNext) {
      page = currentPage + 1;
    } else {
      page = currentPage - 1;
    }

    skip = (page - 1) * values.take;

    setValues({ ...values, skip });
    setCurrentPage(page);
  };

  return (
    <React.Fragment>
      <div className="w-full xl:w-3/5 md:grid md:grid-cols-3 gap-4">
        <KeywordSearch
          placeholder="Zoek"
          onKeyUp={onKeyUpHandler}
          searchValue={searchValue}
          setSearchValue={setSearchValue}
        />

        <div className="w-full pt-4 md:pt-0 flex flex-col md:flex-row justify-between md:justify-start gap-4 md:col-span-2">
          <FilterDropDown
            placeholder="onderwerpen"
            filter="onderwerpen"
            options={filterOptions.onderwerpen}
            onChange={onFilterChanged}
            value={values.onderwerpen}
            nodeType={nodeType}
            trayClass="w-full mt-2"
          />

          <FilterDropDown
            placeholder="gebieden"
            filter="gebieden"
            options={filterOptions.gebieden}
            onChange={onFilterChanged}
            value={values.gebieden}
            nodeType={nodeType}
            trayClass="w-full mt-2"
          />

        </div>
      </div>

      {tags.length > 0 && isFilter && (
        <div className="py-4">
          <h2 className="text-lg mb-3 font-semibold">
            Toegepaste filters
          </h2>
          <ul className="filter-tags--wrapper flex flex-wrap gap-6">
            {tags.map((item) => {

              if (item.filter === "keywords") {

                return (
                  <li key={item.value}>
                    <button
                      className="filter-tag"
                      key={item.value}
                      onClick={() => removeKeyword(item.value)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") removeKeyword(item.value);
                      }}
                      aria-label={`Verwijder trefwoord ${item.value}`}>
                      <span>{item.value}</span>
                      <div
                        className="filter-tag--remove"
                      ></div>
                    </button>
                  </li>
                );
              } else {
                return (
                  <li key={item.value}>
                    <button className="filter-tag"
                      key={`${item.filter}-${item.value}`}
                      onClick={() => onFilterChanged(item.filter, item.value, false)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          onFilterChanged(item.filter, item.value, false);
                        }
                      }}
                      aria-label={`Verwijder filter ${_getSelectedOption(
                        filterOptions[item.filter],
                        item.value,
                        true
                      )?.label}`}
                    >
                      <span>
                        {_getSelectedOption(
                          filterOptions[item.filter],
                          item.value,
                          true
                        ).label}
                      </span>
                      <div className="filter-tag--remove"></div>
                    </button>
                  </li>
                );
              }
            })}
            <li>
              <button className="filter-tag clear cursor-pointer"
                onClick={() => clearFilters()}
                onKeyDown={(event) => {
                  if (event.key === "Enter") clearFilters();
                }}
                aria-label={`Verwijder alle filters`}
              >
                <span>Alles wissen</span>
              </button>
            </li>
          </ul>
        </div>
      )
      }

      <div className="mt-16">
        <div className="container px-0 overflow-x-hidden">
          <div className="my-8">
            {isLoadingPosts && <Loader className="mb-8" />}
            <div className="text-center text-xl md:text-2xl lg:text-3xl mb-8 md:mb-4" role="status" aria-live="polite">
              {totalPosts} resultaten
            </div>

            {/* {_.isArray(posts) && posts.length > 0 ? (
              <React.Fragment>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 justify-items-center gap-8 py-8">
                  {posts.map((post, index) => (
                    <CardLibrary key={index} data={post} />
                  ))}
                </div>

                <div className="slider-control flex gap-4 md:gap-8 items-center justify-center my-12">
                  <button
                    className={`prev rotate-180 nav-icon-after-white text-base gap-7 align-bottom w-full md:w-auto max-w-[100px] py-2.5 px-2 md:px-6 rounded-full bg-black text-white border-black border hover:bg-white hover:text-black relative flex items-center justify-center cursor-pointer ${currentPage <= 1 && "opacity-70 pointer-events-none"
                      }`}
                    aria-label="Previous"
                    onClick={() => navigatePage(false)}
                  ></button>
                  <div className="count-wrap relative">
                    {currentPage} van {Math.ceil(totalPosts / values.take)}
                  </div>
                  <button
                    className={`next nav-icon-after-white text-base gap-7 align-bottom w-full md:w-auto max-w-[100px] py-2.5 px-2 md:px-6 rounded-full bg-black text-white border-black border hover:bg-white hover:text-black relative flex items-center justify-center cursor-pointer ${currentPage >= Math.ceil(totalPosts / values.take) &&
                      "opacity-70 pointer-events-none"
                      }`}
                    aria-label="Next"
                    onClick={() => navigatePage(true)}
                  ></button>
                </div>
              </React.Fragment>
            ) : isLoadingPosts ? null : (
              <p className="text-center">There are no posts available.</p>
            )} */}

            {_.isArray(posts) && posts.length > 0 ? (
              <React.Fragment>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 justify-items-center gap-8 py-8 list-none ps-0">
                  {posts.map((post, index) => (
                    <CardLibrary key={index} data={post} />
                  ))}
                </div>
                {/* Intersection observer target for lazy loading */}
                {posts.length < totalPosts && (
                  <div ref={observerRef} className="w-full h-10 flex items-center justify-center">
                    {isLoadingPosts && <Loader />}
                  </div>
                )}
              </React.Fragment>
            ) : isLoadingPosts ? null : (
              <p className="text-center">There are no posts available.</p>
            )}
          </div>
        </div>
      </div>
      {/* {<StateView title="values" values={values} floating />} */}
      {/* {<StateView title="tags" values={tags} class="bottom-4 left-4 right-[unset]" floating />} */}
      {/* {<StateView title="posts" values={posts} class="top-4 right-4" floating collapsed/>} */}
      {/* {<StateView title="total posts" values={totalPosts} class="top-4 bottom-[unset] left-4 right-[unset]" floating />} */}
      {/* <StateView title="filters" values={filterOptions} class="bottom-4 right-96 max-h-[90vh] overflow-y-auto" floating /> */}
    </React.Fragment>
  );
};

export default Actueel;
